import { useState } from 'react'
import { useTranslation } from '@/i18n'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Eye, Loader2 } from 'lucide-react'
import type { JobRecord } from '@/types/job'

import JobDetailsModal from './JobDetails'
import JobRunModal from './JobRunTableModal'
import JobRunDetailModal from './JobRunDetails'
import { JobStatusTag } from './JobStatusTag'

import { formatDateTime } from '@/utils/timeUtils'
import { useAppStore } from '../store'

const EmptyDataRow = ({ t }: { t: ReturnType<typeof useTranslation>['t'] }) => (
  <TableRow>
    <TableCell colSpan={5} className="text-center">
      {t('common:table_messages.no_jobs_found')}
    </TableCell>
  </TableRow>
)

const JobDataTable = () => {
  const { t } = useTranslation()
  const { jobs, fetchJobRuns } = useAppStore()

  const JOBS_COLS = [
    t('common:table_columns.name'),
    t('common:table_columns.status'),
    t('common:table_columns.created_at'),
    t('common:table_columns.actions'),
  ]

  const [loading, setLoading] = useState(false)
  const [loadingResults, setLoadingResults] = useState(false)
  const [selectedJob, setSelectedJob] = useState<JobRecord | null>(null)
  const [selectedJobRunId, setSelectedJobRunId] = useState<string | 'latest'>(
    'latest'
  )

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isJobRunModalOpen, setIsJobRunModalOpen] = useState(false)
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false)

  const openJobDetails = (job: JobRecord) => {
    setSelectedJob(job)
    setIsModalOpen(true)
  }

  const handleOpenJobRuns = async (job: JobRecord) => {
    setLoading(true)
    setSelectedJob(job)
    await fetchJobRuns(job.id)
    setIsJobRunModalOpen(true)
    setLoading(false)
  }

  const handleOpenResults = async (job: JobRecord) => {
    setLoadingResults(true)
    setSelectedJob(job)
    setSelectedJobRunId('latest')
    await fetchJobRuns(job.id)
    setIsResultsModalOpen(true)
    setLoadingResults(false)
  }

  const onJobRunSelect = (jobRunId: string) => {
    setSelectedJobRunId(jobRunId)
    setIsResultsModalOpen(true)
  }

  return (
    <>
      <Table>
        <TableHeader className="bg-slate-50 dark:bg-dark-muted">
          <TableRow>
            {JOBS_COLS.map((col) => (
              <TableHead
                key={col}
                className={`${
                  col === t('common:table_columns.actions') ? 'w-[100px]' : ''
                }`}
              >
                {col}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.length > 0 ? (
            jobs.map((job) => (
              <TableRow key={job.id}>
                <TableCell>
                  <span className="font-semibold">{job.name}</span>
                </TableCell>
                <TableCell>
                  <JobStatusTag status={job.status} />
                </TableCell>
                <TableCell>{formatDateTime(job.created_at)}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => openJobDetails(job)}>
                      <Eye className="w-4 h-4 mr-1" />
                      {t('common:buttons.details')}
                    </Button>
                    <Button
                      size="sm"
                      className="flex justify-center items-center"
                      onClick={() => handleOpenJobRuns(job)}
                    >
                      {loading && selectedJob === job ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        t('common:buttons.runs')
                      )}
                    </Button>
                    <Button
                      size="sm"
                      className="flex justify-center items-center"
                      onClick={() => handleOpenResults(job)}
                    >
                      {loadingResults && selectedJob === job ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        t('common:buttons.results')
                      )}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <EmptyDataRow t={t} />
          )}
        </TableBody>
      </Table>

      {/* Modals */}
      <JobDetailsModal
        job={selectedJob}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      <JobRunModal
        isOpen={isJobRunModalOpen}
        onClose={() => setIsJobRunModalOpen(false)}
        onRunSelect={onJobRunSelect}
      />
      <JobRunDetailModal
        isOpen={isResultsModalOpen}
        onClose={() => setIsResultsModalOpen(false)}
        jobRunId={selectedJobRunId}
      />
    </>
  )
}

export default JobDataTable
